import { useFormikContext } from 'formik';

import { ErrorField } from '@/components/error-field';
import { RequiredLabel } from '@/components/required-label';
import { ShowHideButton } from '@/components/show-hide';
import { Field } from '@/components/ui/field';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';

import { useToggle } from '@/hooks/use-toggle';

import type { ChangePasswordFormValues } from '@/schemas/user.schema';

import { useFormikError } from '@/lib/error';

export const ChangePasswordForm = () => {
  const { getFieldProps } = useFormikContext<ChangePasswordFormValues>();
  const { state: showCurrentPassword, toggle: toggleCurrentPassword } = useToggle();
  const { state: showNewPassword, toggle: toggleNewPassword } = useToggle();
  const { state: showConfirmPassword, toggle: toggleConfirmPassword } = useToggle();

  const hasError = useFormikError();

  return (
    <div className="flex flex-col gap-4 px-4 py-4 w-full">
      <div className="space-y-2">
        <Field aria-invalid={hasError('current_password')}>
          <RequiredLabel htmlFor="currentPassword">Current password</RequiredLabel>
          <InputGroup>
            <InputGroupInput
              id="currentPassword"
              type={showCurrentPassword ? 'text' : 'password'}
              placeholder="Enter your current password"
              {...getFieldProps('current_password')}
              aria-invalid={hasError('current_password')}
            />
            <InputGroupAddon align="inline-end">
              <ShowHideButton state={showCurrentPassword} toggle={toggleCurrentPassword} />
            </InputGroupAddon>
          </InputGroup>
          <ErrorField name="current_password" />
        </Field>
      </div>
      <div className="space-y-2">
        <Field aria-invalid={hasError('new_password')}>
          <RequiredLabel htmlFor="newPassword">New password</RequiredLabel>
          <InputGroup>
            <InputGroupInput
              id="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              placeholder="Enter your new password"
              {...getFieldProps('new_password')}
              aria-invalid={hasError('new_password')}
            />
            <InputGroupAddon align="inline-end">
              <ShowHideButton state={showNewPassword} toggle={toggleNewPassword} />
            </InputGroupAddon>
          </InputGroup>
          <ErrorField name="new_password" />
        </Field>
      </div>
      <div className="space-y-2">
        <Field aria-invalid={hasError('confirm_new_password')}>
          <RequiredLabel htmlFor="confirmPassword">Confirm new password</RequiredLabel>
          <InputGroup>
            <InputGroupInput
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your new password"
              {...getFieldProps('confirm_new_password')}
              aria-invalid={hasError('confirm_new_password')}
            />
            <InputGroupAddon align="inline-end">
              <ShowHideButton state={showConfirmPassword} toggle={toggleConfirmPassword} />
            </InputGroupAddon>
          </InputGroup>
          <ErrorField name="confirm_new_password" />
        </Field>
      </div>
    </div>
  );
};
